import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Tag, Zap, Crown } from "lucide-react";
import ContactPlanModal from "@/components/ContactPlanModal";

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [billing, setBilling] = useState(null);
  const [selected, setSelected] = useState("yearly");
  const [promo, setPromo] = useState("");
  const [promoState, setPromoState] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const load = async () => {
    const [p, o, b] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/orders"),
      api.get("/billing/me"),
    ]);
    setPlans(p.data); setOrders(o.data); setBilling(b.data);
  };
  useEffect(() => { load(); }, []);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const r = await api.post("/billing/promo", { code: promo, plan_id: selected });
      setPromoState(r.data);
      r.data.valid ? toast.success(`${r.data.discount_pct}% off`) : toast.error(r.data.message);
    } catch { toast.error("Invalid promo"); }
  };

  const subscribe = () => setContactOpen(true);

  const finalAmount = promoState?.valid ? promoState.final_price : (plans.find(p => p.id === selected)?.price || 0);
  const selectedPlan = plans.find(p => p.id === selected);
  const isLifetime = billing?.plan === "lifetime" || billing?.plan === "founder";

  return (
    <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto" data-testid="billing-page">
      <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Billing</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">Subscription</h1>
      {billing && (
        <div className="mt-3 flex items-center gap-3 text-sm">
          <span className="text-zinc-400">Current plan:</span>
          <Badge className={billing.expired ? "bg-red-500/15 text-red-300 border-red-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"}>
            <span className="capitalize">{billing.plan}</span>
            {billing.subscription_ends_at && <span className="ml-2 text-[10px]">{billing.expired ? "expired" : `${billing.days_left}d left`}</span>}
          </Badge>
        </div>
      )}

      {isLifetime && (
        <div className="mt-6 bg-gradient-to-r from-amber-500/[0.08] to-emerald-500/[0.08] border border-amber-500/30 rounded-xl p-5 flex items-center gap-3" data-testid="lifetime-banner">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/40 grid place-items-center">
            <Crown className="w-5 h-5 text-amber-400"/>
          </div>
          <div>
            <div className="font-display text-lg tracking-tight text-amber-200">{billing?.plan === "founder" ? "Founder access" : "Lifetime Pro · Free forever"}</div>
            <div className="text-xs text-zinc-400">Unlimited everything. No billing, no expiry. Welcome to the ipnova family.</div>
          </div>
        </div>
      )}

      {!isLifetime && (
        <div className="grid md:grid-cols-2 gap-4 mt-8">{plans.map(p => {
          const sel = selected === p.id;
          const showFinal = sel && promoState?.valid;
          return (
            <button key={p.id} onClick={() => { setSelected(p.id); setPromoState(null); }}
              className={`text-left rounded-2xl border p-7 transition-all ${sel ? "bg-zinc-950 border-emerald-500/40 ai-glow" : "bg-zinc-950 border-white/10 hover:border-white/20"}`}
              data-testid={`plan-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">{p.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    {showFinal && <span className="text-zinc-600 line-through text-2xl mr-2">₹{p.price.toLocaleString()}</span>}
                    <span className="font-display text-5xl tracking-tighter">₹{(showFinal ? promoState.final_price : p.price).toLocaleString()}</span>
                    <span className="text-zinc-500">/{p.interval}</span>
                  </div>
                  {p.savings_pct && <div className="text-xs text-emerald-400 mt-1">Save {p.savings_pct}% vs monthly</div>}
                </div>
                {sel && <CheckCircle2 className="w-5 h-5 text-emerald-400"/>}
              </div>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/> Unlimited AI agents</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/> Unlimited chats & leads</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/> CRM + analytics</li>
                {p.id === "yearly" && <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/> Priority support · White-label</li>}
              </ul>
            </button>
          );
        })}</div>
      )}

      {!isLifetime && (
        <>
          <div className="mt-6 bg-zinc-950 border border-white/10 rounded-xl p-5">
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2 flex items-center gap-2"><Tag className="w-3.5 h-3.5 text-emerald-400"/> Promo code</div>
            <div className="flex gap-2">
              <input value={promo} onChange={e => setPromo(e.target.value.toUpperCase())} placeholder="Enter code (try IP3725)"
                className="flex-1 bg-transparent border border-white/10 rounded-md px-3 h-10 focus:border-white/30 outline-none text-sm font-mono" data-testid="promo-input"/>
              <Button onClick={applyPromo} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 h-10" data-testid="promo-apply">Apply</Button>
            </div>
            {promoState?.valid && <div className="mt-3 text-sm text-emerald-400">✓ {promoState.code} — saves ₹{promoState.savings.toLocaleString()}</div>}
          </div>

          <div className="mt-6 flex items-center justify-between bg-emerald-500/[0.04] border border-emerald-500/20 rounded-xl p-5 gap-4 flex-wrap">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-emerald-400 mb-1">Activate plan</div>
              <div className="font-display text-2xl tracking-tighter">Total: ₹{finalAmount.toLocaleString()}</div>
              <div className="text-xs text-zinc-500 mt-0.5">Payments rolling out soon — for now we activate manually within hours.</div>
            </div>
            <Button onClick={subscribe} className="bg-white text-black hover:bg-zinc-200 h-11 px-6" data-testid="subscribe-btn">
              <Zap className="w-4 h-4 mr-2"/> Contact to activate
            </Button>
          </div>
        </>
      )}

      <div className="mt-8">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Order history</div>
        {orders.length === 0 ? <div className="text-zinc-600 text-sm">No orders yet.</div>
          : <div className="bg-zinc-950 border border-white/10 rounded-xl overflow-hidden">
            {orders.map((o, i) => (
              <div key={o.id} className={`flex items-center justify-between px-5 py-3 text-sm ${i ? "border-t border-white/5" : ""}`} data-testid={`order-${o.id}`}>
                <div>
                  <div className="font-mono text-xs text-zinc-400">{o.razorpay_order_id}</div>
                  <div className="text-xs text-zinc-600">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-base">₹{o.amount.toLocaleString()}</div>
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] mt-0.5 capitalize">{o.status}</Badge>
                </div>
              </div>
            ))}
          </div>}
      </div>

      <ContactPlanModal open={contactOpen} onClose={() => setContactOpen(false)} planName={selectedPlan?.name}/>
    </div>
  );
}
